import React, { useState } from 'react';
import USBCheckbox from '@usb-shield/react-forms-checkbox';
import USBSearchInput from '@usb-shield/react-search-input';
import Spinner from '../../components/shared/Spinner';
import { getValueByNestedKey, isEmployee } from './utils';

export default function InboxTable({
  data,
  headers,
  status,
  setStatus,
  searchProperties,
  tableActions,
  onRowClick,
}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortAscending, setSortAscending] = useState(true);
  const [hoverIndex, setHoverIndex] = useState(-1);

  const selectAllRef = React.createRef();

  const { loading } = status;

  const handleRowSelection =
    (rowId) =>
    ({ event }) => {
      if (event === 'onChange') {
        const selectedRow = selectedRows.includes(rowId);
        if (selectedRow) {
          setSelectedRows(selectedRows.filter((each) => each !== rowId));
        } else {
          setSelectedRows([...selectedRows, rowId]);
        }
      }
    };
  const handleSelectAll = ({ event, inputRef }) => {
    if (event === 'onChange') {
      if (selectedRows.length === data.length) {
        setSelectedRows([]);
      } else {
        setSelectedRows(
          data.map((row) => {
            return getValueByNestedKey(row, tableActions.selectionKey);
          })
        );
      }
    }
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const handleSort = (accessor) => () => {
    setSortColumn(accessor);
    setSortAscending(!sortAscending);
  };

  function onHeaderMouseEnter(index) {
    return () => setHoverIndex(index);
  }
  function onHeaderMouseLeave() {
    setHoverIndex(null);
  }

  // ISO date strings (e.g. "2025-10-06T00:00:00.000Z") only match search terms
  // that happen to be substrings of "YYYY-MM-DD". This also generates the
  // MM-DD-YYYY / MM/DD/YYYY forms so full displayed dates are searchable too.
  const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}/;
  function getSearchableValues(value) {
    if (typeof value !== 'string') return [value];
    const values = [value];
    if (ISO_DATE_REGEX.test(value)) {
      const [year, month, day] = value.split('T')[0].split('-');
      values.push(`${month}-${day}-${year}`, `${month}/${day}/${year}`);
    }
    return values;
  }

  // Function to filter the data based on the search term
  const filteredData =
    searchProperties.enable && searchTerm?.toLowerCase()
      ? data.filter((row) => {
          return searchProperties.keys.some((eachKey) => {
            return getSearchableValues(getValueByNestedKey(row, eachKey)).some(
              (value) => value?.toLowerCase()?.includes(searchTerm?.toLowerCase())
            );
          });
        })
      : data;

  // Function to sort the data based on the selected column
  const sortedData =
    sortColumn !== null
      ? [...filteredData].sort((a, b) => {
          const valueA = getValueByNestedKey(a, sortColumn);
          const valueB = getValueByNestedKey(b, sortColumn);
          if (valueA < valueB) {
            return sortAscending ? -1 : 1;
          } else if (valueA > valueB) {
            return sortAscending ? 1 : -1;
          }
          return 0;
        })
      : filteredData;

  const isEmptyResults = sortedData?.length === 0 && !!searchTerm;
  const TableActionComponents = tableActions?.Components
    ? tableActions.Components
    : [];

  return (
    <div className="closing-binder-table ">
      <div className="table-container">
        <section className="table-actions-container">
          <section className="table-actions-left">
            {searchProperties.enable ? (
              <div className="search-bar">
                <USBSearchInput
                  id="cb-table-search"
                  name="test-name"
                  addClasses="cb-table-search"
                  type="search"
                  isError={false}
                  ariaProps={{
                    label: 'Search',
                  }}
                  placeholder="Search"
                  handleChange={handleSearchChange}
                />
              </div>
            ) : null}
          </section>
          {TableActionComponents.length > 0 ? (
            <section className="table-actions-right">
              <p className="p-r-lg"> Selected {selectedRows.length} files</p>
              {TableActionComponents.map((Component, index) => (
                <Component
                  key={index}
                  selectedRows={selectedRows}
                  data={data}
                />
              ))}
            </section>
          ) : null}
        </section>
        <section className="cb-list-table-container">
          <table role="table">
            <thead>
              <tr>
                {tableActions?.enable ? (
                  <th scope="col" style={{ width: '5%' }}>


                    <USBCheckbox
                     labelText=' '
                     subLabel=""
                     errorMessages='Please check the box'
                      inputName="CB-documents-select-all"
                      statusUpdateCallback={handleSelectAll}
                      addClasses="cb-checkbox"
                      ref={selectAllRef}
                      dynamicValue={selectedRows.length === data?.length}
                    />
                  </th>
                ) : null}
                {headers.map((header, index) => {
                  if (header.sortable) {
                    return (
                      <th
                        key={index}
                        scope="col"
                        role="button"
                        onClick={handleSort(header.accessor)}
                        style={header.style || {}}
                        onMouseEnter={onHeaderMouseEnter(index)}
                        onMouseLeave={onHeaderMouseLeave}
                        className="pointer"
                      >
                        {header.label}
                        {sortColumn === header.accessor ? (
                          <span>{sortAscending ? ' ▲' : ' ▼'}</span>
                        ) : hoverIndex === index ? (
                          <span> ▼</span>
                        ) : null}
                      </th>
                    );
                  }

                  return (
                    <th key={index} scope="col" style={header.style || {}}>
                      {header.label}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {sortedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  role="row"
                  className={onRowClick ? 'inbox-row-clickable' : undefined}
                  title={onRowClick ? 'Click to view more details' : undefined}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {tableActions?.enable ? (
                    <td role="cell" onClick={(e) => e.stopPropagation()}>
                      <USBCheckbox
                        labelText=' '
                        subLabel=""
                        inputName={`CB-documents-select-row-${row.id}`}
                        statusUpdateCallback={handleRowSelection(
                          getValueByNestedKey(row, tableActions.selectionKey)
                        )}
                        addClasses="cb-checkbox"
                        dynamicValue={selectedRows.includes(
                          getValueByNestedKey(row, tableActions.selectionKey)
                        )}
                      />
                    </td>
                  ) : null}
                  {headers.map((header, cellIndex) => (
                    <td
                      key={cellIndex}
                      role="cell"
                      style={header.style || {}}
                      onClick={header.disableRowClick ? (e) => e.stopPropagation() : undefined}
                    >
                      {header.cell
                        ? header.cell(row)
                        : getValueByNestedKey(row, header.accessor)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {isEmptyResults ? (
            <div>
              <h4 style={{ width: '100%', textAlign: 'center' }}>
                No results found.
              </h4>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

InboxTable.defaultValues = {
  searchProperties: {
    enable: false,
    keys: [],
  },
  tableActions: {
    enable: false,
    selectionKey: 'id',
  },
};
